import parse from 'url-parse';
import type { MetaGetter } from '@atlassian/jira-get-meta';
import { getScopeIdentifier } from '../get-scope-identifier/index.tsx';
import connectRoutes from '../routes/connect.tsx';
import projectSettingsConnectRoutes from '../routes/project-settings-connect.tsx';
import projectSettingsRoutes from '../routes/project-settings.tsx';
import type { ScopeData, ProjectKey, BoardId, PageType } from './types.tsx';

type ScopeDataConfig = {
	routes: RegExp[];
	meta: string[];
};

const projectScopeDataMap: ScopeDataConfig = {
	routes: [
		// Query string
		/[&?]projectKey=(\w+)/i,
		// Service desk, business and some software
		/\/projects\/(\w+)/i,
		// Issue page (not root)
		/\/browse\/(\w+)-\d+\??((?!(filter=|jql=)).)*$/i,
		// Project settings
		/\/plugins\/servlet\/project-config\/(\w+)/i,
		// Service Desk project settings
		/\/servicedesk\/admin\/(\w+)/i,
		// Browse project
		/\/browse\/(\w+)\/?(\?.*)?$/i,
	],
	meta: [
		// Software
		'ghx-project-key',
		// Settings
		'projectKey',
		// Reports
		'ajs-project-key',
	],
};

const getProjectId = (url: string): string | null => {
	const projectIdRoutes: RegExp[] = [
		// Query string - reports
		/[&?]selectedProjectId=(\w+)/i,
		// Query string - edit project, delete project
		/[&?]pid=(\w+)/i,
		// Query string - release notes
		/[&?]projectId=(\w+)/i,
		// Query string - connect items
		/[&?]project\.id=(\w+)/i,
	];
	return projectIdRoutes.reduce((foundId: string | null, route: RegExp): string | null => {
		if (foundId) return foundId;

		const match = url.match(route);
		if (match != null) {
			const [, projectId] = match;

			if (projectId) return projectId;
		}

		return null;
	}, null);
};

/**
 * Extracts certain data out of the URL that is required by other data providers.
 */

const getProjectKey = (url: string, getMeta: MetaGetter): ProjectKey | undefined => {
	let projectKey: string | null = null;
	projectScopeDataMap.meta.forEach((tagName) => {
		const value = getMeta(tagName);
		if (value && projectKey == null) {
			projectKey = value;
		}
	});
	projectScopeDataMap.routes.forEach((route) => {
		const match = url.match(route);
		if (match != null && projectKey == null) {
			[, projectKey] = match;
		}
	});
	return projectKey || undefined;
};

export const getBoardId = (url: string): BoardId | null => {
	try {
		const parsed = parse(url, true);
		let boardId = parsed.query ? parsed.query.rapidView : null;
		if (boardId == null) {
			const match = url.match(/\/boards\/(\d+)/i);
			boardId = match ? match[1] : null;
		}
		return boardId;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (err: any) {
		return null;
	}
};

const isProjectSettingsConnectRoute = ({ getMeta, url }: { getMeta: MetaGetter; url: string }) =>
	(getMeta('ajs-in-admin-mode') === 'true' && connectRoutes.some((regex) => regex.test(url))) ||
	projectSettingsConnectRoutes.some((regex) => regex.test(url));

const isProjectPage = ({
	getMeta,
	projectId,
	projectKey,
	url,
}: {
	getMeta: MetaGetter;
	projectId: string | null | undefined;
	projectKey: string | null | undefined;
	url: string;
}) => {
	// Project routes that have a project id in the URL
	const isProject = () => {
		const routes = [
			/\/secure\/ConfigureReport(!default)?\.jspa.+selectedProjectId/i,
			/\/secure\/project\/(DeleteProject|EditProject)!default\.jspa.+pid/i,
			/\/secure\/ReleaseNote.jspa.+projectId/i,
			/\/secure\/ConfigureReleaseNote.jspa.+projectId/i,
		];
		return (
			isProjectSettingsConnectRoute({ getMeta, url }) || routes.some((regex) => regex.test(url))
		);
	};
	return projectKey || (projectId && isProject());
};

const isProjectSettingsPage = ({ getMeta, url }: { getMeta: MetaGetter; url: string }) =>
	projectSettingsRoutes.some((route) => url.match(route)) ||
	isProjectSettingsConnectRoute({ getMeta, url });

const getProjectPageType = (getMeta: MetaGetter, url: string): PageType => {
	if (isProjectSettingsPage({ getMeta, url })) {
		return 'settings';
	}
	const decorator = getMeta('decorator');
	const isSettings = getMeta('is-settings-page') === 'true';

	return decorator === 'admin' || decorator === 'atl.admin' || isSettings ? 'settings' : undefined;
};

const getScopeData = (url: string, getMeta: MetaGetter): ScopeData => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const extractedData: Record<string, any> = {};

	const boardId = getBoardId(url);
	if (boardId) {
		extractedData.boardId = boardId;
	}

	const projectKey =
		url.includes('/plugins/servlet/ac/') || url.includes('/settings/projects/')
			? undefined
			: getProjectKey(url, getMeta);

	if (projectKey) {
		extractedData.projectKey = projectKey;
	}

	const projectId = getProjectId(url);
	if (projectId) {
		extractedData.projectId = projectId;
	}

	const isProject = isProjectPage({ getMeta, projectId, projectKey, url });

	extractedData.pageType = isProject ? getProjectPageType(getMeta, url) : undefined;

	const identifier = getScopeIdentifier({
		boardId,
		projectKey,
		projectId: isProject ? projectId : null,
	});

	// This gets around a flow inexact type error
	return { ...extractedData, ...identifier };
};

export default getScopeData;
